import { Redo2, Undo2, X } from 'lucide-react';
import type { SeatingActions } from './useSeatingActions';

/**
 * What the last seating change did (or why it did not), with its Undo — the
 * seating screens' own toast, since a seat moved by a drag deserves a way back
 * right where the eye is.
 */
export function SeatingNoticeBar({ actions }: { actions: SeatingActions }) {
  const { notice, history, undo, setNotice } = actions;
  if (!notice) return null;
  return (
    <div
      role="status"
      className={`theme-light fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-lg px-4 py-2 text-sm shadow-lg ${
        notice.tone === 'error' ? 'bg-red-700 text-white' : 'bg-gray-900 text-white'
      }`}
      data-testid="seating-notice"
    >
      <span>{notice.text}</span>
      {notice.undo && history.canUndo && (
        <button type="button" onClick={() => void undo()} className="font-semibold underline">
          Undo
        </button>
      )}
      <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
        <X size={14} />
      </button>
    </div>
  );
}

/** Undo and redo for seating changes, named by what they would undo. */
export function UndoRedoButtons({ actions }: { actions: SeatingActions }) {
  const { history, busy, undo, redo } = actions;
  return (
    <>
      <button
        type="button"
        onClick={() => void undo()}
        disabled={!history.canUndo || busy}
        aria-label="Undo"
        title={history.nextUndo ? `Undo: ${history.nextUndo} (Ctrl+Z)` : 'Undo (Ctrl+Z)'}
        className="rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
      >
        <Undo2 size={16} />
      </button>
      <button
        type="button"
        onClick={() => void redo()}
        disabled={!history.canRedo || busy}
        aria-label="Redo"
        title="Redo (Ctrl+Shift+Z)"
        className="rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
      >
        <Redo2 size={16} />
      </button>
    </>
  );
}
